import React, { useEffect, useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  FaArrowRight, FaShieldAlt, FaBolt, FaLock, FaChartLine, FaCheck, FaAngleRight,
} from 'react-icons/fa';
import PageNavbar from './PageNavbar';
import Footer from './Footer';
import {
  defaultViewport,
  staggerContainer,
  staggerItem,
  getRevealProps,
} from '../motion/scrollMotion';
import { getServiceBySlug, categoryMeta, allServices } from '../config/servicesMenuConfig';
import { useRegion } from '../context/RegionContext';
import './ServiceDetailPage.css';

const featureIcons = [<FaShieldAlt />, <FaBolt />, <FaLock />, <FaChartLine />];

// Colour a terminal line by its shape ($ command, ! finding, ✓/→ result).
const termLineClass = (l) => {
  const t = l.trimStart();
  if (l.startsWith('$')) return 'svc-term-cmd';
  if (t.startsWith('!')) return 'svc-term-crit';
  if (t.startsWith('→') || t.startsWith('✓')) return 'svc-term-ok';
  return 'svc-term-out';
};

// Typewriter terminal for the "console" hero — types the lines, then loops.
const ConsoleTerminal = ({ lines }) => {
  const [pos, setPos] = useState({ l: 0, c: 0 });
  useEffect(() => {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setPos({ l: lines.length, c: 0 });
      return undefined;
    }
    let l = 0, c = 0, timer;
    const step = () => {
      if (l >= lines.length) {
        timer = setTimeout(() => { l = 0; c = 0; setPos({ l, c }); step(); }, 3200);
        return;
      }
      if (c < lines[l].length) {
        c += 1; setPos({ l, c });
        timer = setTimeout(step, 20 + Math.random() * 30);
      } else {
        l += 1; c = 0; setPos({ l, c });
        timer = setTimeout(step, 260);
      }
    };
    timer = setTimeout(step, 500);
    return () => clearTimeout(timer);
  }, [lines]);

  return (
    <div className="svc-term" aria-hidden="true">
      {lines.map((line, i) => {
        if (i > pos.l) return null;
        const text = i < pos.l ? line : line.slice(0, pos.c);
        return (
          <div className={`svc-term-ln ${termLineClass(line)}`} key={i}>
            {text || ' '}
            {i === pos.l && <span className="svc-term-cur" />}
          </div>
        );
      })}
    </div>
  );
};

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const { region } = useRegion();
  const service = getServiceBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (service) {
      document.title = service.seoTitle || `${service.label} | Proven InfoSec`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', service.seoDesc || service.description);
      } else {
        const metaEl = document.createElement('meta');
        metaEl.name = 'description';
        metaEl.content = service.seoDesc || service.description;
        document.head.appendChild(metaEl);
      }
    }
  }, [slug, service]);

  // Structured data (JSON-LD) for SEO: Service + BreadcrumbList, plus FAQPage
  // when the service defines FAQs. Managed in a single <script> tag that is
  // rebuilt per route and removed on unmount.
  useEffect(() => {
    if (!service) return undefined;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const graph = [
      {
        '@type': 'Service',
        name: service.label,
        serviceType: service.category,
        description: service.seoDesc || service.description,
        provider: { '@type': 'Organization', name: 'Proven InfoSec' },
        url: `${origin}${service.href}`,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
          { '@type': 'ListItem', position: 2, name: 'Services', item: `${origin}/services` },
          { '@type': 'ListItem', position: 3, name: service.label, item: `${origin}${service.href}` },
        ],
      },
    ];
    if (service.faqs && service.faqs.length > 0) {
      graph.push({
        '@type': 'FAQPage',
        mainEntity: service.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      });
    }
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'svc-jsonld';
    script.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
    document.getElementById('svc-jsonld')?.remove();
    document.head.appendChild(script);
    return () => document.getElementById('svc-jsonld')?.remove();
  }, [slug, service]);

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const meta = categoryMeta[service.category];
  const features = service.features || (meta && meta.features) || [];
  const processSteps = service.process || (meta && meta.process) || [];
  const whySection = service.why || (meta && meta.why) || null;
  const stats = service.stats || (meta && meta.stats) || [];
  const standards = service.standards || [];
  const faqs = service.faqs || [];
  // Offensive pages funnel to a pentest quote; everything else to all services.
  const isOffensive = service.category === 'Offensive Security';
  const secondaryCta = isOffensive
    ? { to: '/pentest-quote', label: 'Get a Quote' }
    : { to: '/services', label: 'All Services' };
  const related = allServices.filter(
    (s) => s.category === service.category
      && s.slug !== service.slug
      && (!s.region || s.region === region)
  );

  // Per-service section copy so each page reads uniquely instead of sharing one
  // templated subtitle. Driven by the service's own standards + phase count.
  const topStandards = standards.slice(0, 2).join(' and ');
  const whatsIncludedSub = topStandards
    ? `Every ${service.label} engagement is scoped to your environment and aligned to ${topStandards} — never a generic checklist.`
    : 'A focused, expert-led service built around measurable security outcomes — never a generic checklist.';
  const processSub = processSteps.length
    ? `A ${processSteps.length}-phase, expert-led methodology tailored to ${service.label} — transparent, on-track and outcome-driven.`
    : 'A repeatable approach that keeps every engagement transparent, on-track, and outcome-driven.';

  return (
    <div className="svc-page">
      <PageNavbar />

      {service.heroStyle === 'cinematic' && service.image ? (
        /* ---------- Cinematic hero: image as a full-bleed backdrop ---------- */
        <section className="svc-cine">
          <div className="svc-cine-bg">
            <img src={`${process.env.PUBLIC_URL}/${service.image}`} alt="" aria-hidden="true" />
          </div>
          <div className="svc-cine-grid" aria-hidden="true" />
          <div className="svc-container svc-cine-inner">
            <motion.div
              className="svc-cine-copy"
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <nav className="svc-breadcrumb" aria-label="Breadcrumb">
                <Link to="/">Home</Link><FaAngleRight />
                <Link to="/services">Services</Link><FaAngleRight />
                <span>{service.label}</span>
              </nav>
              <span className="svc-eyebrow">{service.category}</span>
              <h1 className="svc-cine-title">{service.label}</h1>
              <div className="svc-cine-rule" />
              <p className="svc-cine-desc">{service.description}</p>
              <div className="svc-hero-actions">
                <Link to="/contact" className="svc-btn svc-btn-primary">Get In Touch <FaArrowRight /></Link>
                <Link to={secondaryCta.to} className="svc-btn svc-btn-ghost">{secondaryCta.label}</Link>
              </div>
              {standards.length > 0 && (
                <div className="svc-cine-chips">
                  {standards.slice(0, 4).map((s) => <span key={s}>{s}</span>)}
                </div>
              )}
            </motion.div>
          </div>
        </section>
      ) : service.heroStyle === 'console' && service.image ? (
        /* ---------- Console hero: image framed as a target app + terminal ---------- */
        <section className="svc-console">
          <div className="svc-container svc-console-inner">
            <motion.div
              className="svc-console-left"
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <nav className="svc-breadcrumb" aria-label="Breadcrumb">
                <Link to="/">Home</Link><FaAngleRight />
                <Link to="/services">Services</Link><FaAngleRight />
                <span>{service.label}</span>
              </nav>
              <span className="svc-eyebrow">{service.category}</span>
              <h1 className="svc-console-title">{service.label}</h1>
              <p className="svc-console-desc">{service.description}</p>
              {service.console && <ConsoleTerminal lines={service.console} />}
              <div className="svc-hero-actions">
                <Link to="/contact" className="svc-btn svc-btn-primary">Get In Touch <FaArrowRight /></Link>
                <Link to={secondaryCta.to} className="svc-btn svc-btn-ghost">{secondaryCta.label}</Link>
              </div>
            </motion.div>

            <motion.div
              className="svc-console-visual"
              initial={{ opacity: 0, x: 34, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 1, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="svc-win">
                <div className="svc-win-bar">
                  <span className="svc-win-dot r" /><span className="svc-win-dot y" /><span className="svc-win-dot g" />
                  <span className="svc-win-url">https://app.example.com</span>
                </div>
                <div className="svc-win-screen">
                  <img src={`${process.env.PUBLIC_URL}/${service.image}`} alt={service.label} />
                  <div className="svc-win-scan" aria-hidden="true" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      ) : (
        <>
          {/* ---------- Full-width top banner image (when provided) ---------- */}
          {service.image && (
            <section className="svc-topbanner">
              <img
                className="svc-topbanner-img"
                src={`${process.env.PUBLIC_URL}/${service.image}`}
                alt={service.label}
                onError={(e) => {
                  const banner = e.currentTarget.closest('.svc-topbanner');
                  if (banner) banner.style.display = 'none';
                }}
              />
            </section>
          )}

          {/* ---------- Hero ---------- */}
          <section className="svc-hero">
            <div className="svc-hero-glow" />
            <div className="svc-container svc-hero-inner">
              <motion.div className="svc-hero-text" {...getRevealProps('fadeUp')}>
                <nav className="svc-breadcrumb" aria-label="Breadcrumb">
                  <Link to="/">Home</Link>
                  <FaAngleRight />
                  <Link to="/services">Services</Link>
                  <FaAngleRight />
                  <span>{service.label}</span>
                </nav>

                <span className="svc-eyebrow">{service.category}</span>
                <h1 className="svc-hero-title">{service.label}</h1>
                <p className="svc-hero-desc">{service.description}</p>

                <div className="svc-hero-actions">
                  <Link to="/contact" className="svc-btn svc-btn-primary">
                    Get In Touch <FaArrowRight />
                  </Link>
                  <Link to="/services" className="svc-btn svc-btn-ghost">
                    All Services
                  </Link>
                </div>
              </motion.div>

              {(stats.length > 0 || features.length > 0) && (
                <motion.aside className="svc-hero-card" {...getRevealProps('slideRight', 0.12)}>
                  <span className="svc-card-tag">At a glance</span>
                  <div className="svc-stats">
                    {stats.map((s) => (
                      <div className="svc-stat" key={s.label}>
                        <div className="svc-stat-value">{s.value}</div>
                        <div className="svc-stat-label">{s.label}</div>
                      </div>
                    ))}
                  </div>
                  <ul className="svc-card-list">
                    {features.map((feat) => (
                      <li key={feat.title}>
                        <FaCheck /> {feat.title}
                      </li>
                    ))}
                  </ul>
                </motion.aside>
              )}
            </div>
          </section>
        </>
      )}

      {/* ---------- Methodology & standards strip ---------- */}
      {standards.length > 0 && (
        <section className="svc-standards">
          <div className="svc-container svc-standards-inner">
            <span className="svc-standards-label">Tested against</span>
            <ul className="svc-standards-chips">
              {standards.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* ---------- What's Included ---------- */}
      <section className="svc-section">
        <div className="svc-container">
          <motion.header className="svc-section-head" {...getRevealProps('fadeUp')}>
            <span className="svc-section-tag">What’s Included</span>
            <h2 className="svc-section-title">Everything in your {service.label} engagement</h2>
            <p className="svc-section-sub">{whatsIncludedSub}</p>
          </motion.header>

          <motion.div
            className="svc-feature-grid"
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
            variants={{
              ...staggerContainer,
              visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
            }}
          >
            {features.map((feat, i) => (
              <motion.article className="svc-feature-card" key={feat.title} variants={staggerItem}>
                <div className="svc-feature-icon">{featureIcons[i % featureIcons.length]}</div>
                <h3 className="svc-feature-title">{feat.title}</h3>
                <p className="svc-feature-desc">{feat.desc}</p>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ---------- Process ---------- */}
      {processSteps.length > 0 && (
        <section className="svc-section svc-section-alt">
          <div className="svc-container">
            <motion.header className="svc-section-head" {...getRevealProps('fadeUp')}>
              <span className="svc-section-tag">How We Work</span>
              <h2 className="svc-section-title">A clear, proven process</h2>
              <p className="svc-section-sub">{processSub}</p>
            </motion.header>

            <motion.ol
              className="svc-process"
              initial="hidden"
              whileInView="visible"
              viewport={defaultViewport}
              variants={{
                ...staggerContainer,
                visible: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } },
              }}
            >
              {processSteps.map((m) => (
                <motion.li className="svc-step" key={m.step} variants={staggerItem}>
                  <div className="svc-step-num">{m.step}</div>
                  <h3 className="svc-step-name">{m.name}</h3>
                  <p className="svc-step-desc">{m.desc}</p>
                </motion.li>
              ))}
            </motion.ol>
          </div>
        </section>
      )}

      {/* ---------- Why it matters ---------- */}
      {whySection && (
        <section className="svc-section">
          <div className="svc-container svc-why-grid">
            <motion.div className="svc-why-text" {...getRevealProps('slideLeft')}>
              <span className="svc-section-tag">Why It Matters</span>
              <h2 className="svc-section-title">{whySection.title}</h2>
              <p className="svc-why-body">{whySection.body}</p>
            </motion.div>

            <motion.ul className="svc-why-list" {...getRevealProps('slideRight', 0.12)}>
              {whySection.bullets.map((b) => (
                <li key={b}>
                  <span className="svc-why-check"><FaCheck /></span>
                  {b}
                </li>
              ))}
            </motion.ul>
          </div>
        </section>
      )}

      {/* ---------- Related services ---------- */}
      {related.length > 0 && (
        <section className="svc-section svc-section-alt">
          <div className="svc-container">
            <motion.header className="svc-section-head" {...getRevealProps('fadeUp')}>
              <span className="svc-section-tag">Explore More</span>
              <h2 className="svc-section-title">More in {service.category}</h2>
            </motion.header>

            <div className="svc-related-grid">
              {related.map((r) => (
                <Link to={r.href} className="svc-related-card" key={r.slug}>
                  <span className="svc-related-name">{r.label}</span>
                  <FaArrowRight />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------- FAQ ---------- */}
      {faqs.length > 0 && (
        <section className="svc-section">
          <div className="svc-container">
            <motion.header className="svc-section-head" {...getRevealProps('fadeUp')}>
              <span className="svc-section-tag">FAQ</span>
              <h2 className="svc-section-title">Frequently asked questions</h2>
              <p className="svc-section-sub">
                The questions we hear most about {service.label.toLowerCase()} — answered by our
                testing team.
              </p>
            </motion.header>

            <motion.div
              className="svc-faq-list"
              initial="hidden"
              whileInView="visible"
              viewport={defaultViewport}
              variants={{
                ...staggerContainer,
                visible: { transition: { staggerChildren: 0.06, delayChildren: 0.05 } },
              }}
            >
              {faqs.map((f) => (
                <motion.div className="svc-faq-item" key={f.q} variants={staggerItem}>
                  <h3 className="svc-faq-q">{f.q}</h3>
                  <p className="svc-faq-a">{f.a}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* ---------- CTA ---------- */}
      <section className="svc-cta">
        <div className="svc-container">
          <motion.div className="svc-cta-card" {...getRevealProps('scaleUp')}>
            <h2 className="svc-cta-title">Ready to get started with {service.label}?</h2>
            <p className="svc-cta-desc">
              Talk to one of our certified security professionals about a tailored engagement — we
              respond to every inquiry within one business day.
            </p>
            <div className="svc-cta-actions">
              <Link to="/contact" className="svc-btn svc-btn-primary">
                Book Consultation <FaArrowRight />
              </Link>
              <Link to="/services" className="svc-btn svc-btn-ghost-light">
                View All Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceDetailPage;
